import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { useTranslation } from '@aimsos/i18n';
import { Button, CartContainer, Pagination } from '@aimsos/ui';

import DataListFilterBar from '../components/DataListFilterBar.jsx';
import DataListFilterSlideout from '../components/DataListFilterSlideout.jsx';
import EntityListSkeleton from '../components/EntityListSkeleton.jsx';
import DriveExtractionToggle from '../components/knowledge/drive/DriveExtractionToggle.jsx';
import DriveHeader from '../components/knowledge/drive/DriveHeader.jsx';
import DriveItemCard from '../components/knowledge/drive/DriveItemCard.jsx';
import DriveTree from '../components/knowledge/drive/DriveTree.jsx';
import NewFolderSlideOut from '../components/knowledge/drive/NewFolderSlideOut.jsx';
import KnowledgeQuickFilters from '../components/knowledge/KnowledgeQuickFilters.jsx';
import { AccessDeniedState, EmptyState, ErrorState } from '../components/states/index.js';
import {
  DRIVE_CLAIMS_SOURCE_PARAM,
  DRIVE_CLAIMS_SUBTAB,
  DRIVE_FOLDER_PARAM,
  DRIVE_PAGE_SIZE_OPTIONS,
  DRIVE_QUICK_FILTERS,
  DRIVE_ROOT_ID,
  DRIVE_SCOPES,
  DRIVE_SEARCH_FIELDS,
  DRIVE_SORT_DIRECTION,
  DRIVE_SORT_FIELD,
  DRIVE_TYPES,
} from '../constants/knowledge.js';
import { useDataListFilters } from '../hooks/useDataListFilters.js';
import { useEntityDrive } from '../hooks/useEntityDrive.js';
import { useListPagination, usePaginationLabels } from '../hooks/useListPagination.js';
import { useTabPath } from '../hooks/useTabPath.js';
import { useSetDriveExtraction } from '../hooks/useSetDriveExtraction.js';

import { FolderOpen, SearchX } from 'lucide-react';

const FILTER_DEFAULTS = Object.freeze({
  defaultSortField: DRIVE_SORT_FIELD,
  defaultSortDirection: DRIVE_SORT_DIRECTION,
  searchFields: DRIVE_SEARCH_FIELDS,
});

/**
 * Knowledge › Drive (CT-5717): the record's OWN drive.
 *
 * The left tree ("This record") and, inside a sub-folder, the breadcrumb move
 * between folders; the folder in view is `?folder=<id>`, so Back returns to the
 * parent. Above the rows: the folder's name and owner, New folder (for whoever
 * may manage the drive), Upload (not designed yet, disabled), and the claim
 * extraction switch with its counter. Search, Most recent, the chips and All
 * filters (Search in, Type, Created by — staged) narrow on the server, with the
 * domain's pinned pager below.
 *
 * "View claims" opens Sandbox filtered by that file (CT-5718).
 */
function DrivePage() {
  const { t } = useTranslation('unified-profiles');
  const { entityType, entityId } = useOutletContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const folderId = searchParams.get(DRIVE_FOLDER_PARAM) ?? DRIVE_ROOT_ID;

  const [quickFilter, setQuickFilter] = useState('all');
  const [isAllFiltersOpen, setIsAllFiltersOpen] = useState(false);
  const [isNewFolderOpen, setIsNewFolderOpen] = useState(false);

  const filtersState = useDataListFilters(FILTER_DEFAULTS);
  const { filters, search, orderDirection, hasActiveQuery, clearAll } = filtersState;

  // The domain's pager: a different folder or query is a shorter list, back to page 1.
  const queryKey = `${folderId}|${quickFilter}|${orderDirection}|${JSON.stringify(filters ?? {})}|${search?.query ?? ''}`;
  const { page, pageSize, handlePrevPage, handleNextPage, handlePageSizeChange } = useListPagination({
    pageSizeOptions: DRIVE_PAGE_SIZE_OPTIONS,
    queryKey,
  });

  const { data, meta, loading, error, accessDenied, refetch, refresh, setExtractionEnabled } = useEntityDrive({
    entityType,
    entityId,
    folderId,
    page,
    pageSize,
    orderDirection,
    quickFilter,
    filters,
    search,
  });

  const openFolder = useCallback(
    (id) =>
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        if (id === DRIVE_ROOT_ID) next.delete(DRIVE_FOLDER_PARAM);
        else next.set(DRIVE_FOLDER_PARAM, id);
        return next;
      }),
    [setSearchParams],
  );

  // "View claims" → the Sandbox subtab, a sibling of this route.
  const navigate = useNavigate();
  const tabPath = useTabPath();
  const viewClaims = useCallback(
    (fileId) => navigate(`${tabPath}/${DRIVE_CLAIMS_SUBTAB}?${DRIVE_CLAIMS_SOURCE_PARAM}=${encodeURIComponent(fileId)}`),
    [navigate, tabPath],
  );

  const currentName = data?.folder?.name ?? data?.driveName ?? '';
  const { set: setExtraction, saving: savingExtraction } = useSetDriveExtraction({
    entityType,
    entityId,
    onOptimistic: setExtractionEnabled,
    onRollback: setExtractionEnabled,
  });
  const handleExtraction = useCallback(
    (enabled) => setExtraction({ folderId, enabled, name: currentName }),
    [setExtraction, folderId, currentName],
  );

  const filterDefs = useMemo(
    () => [
      {
        id: 'scope',
        label: t('knowledge.drive.filters.scope'),
        options: DRIVE_SCOPES.map((value) => ({ value, label: t(`knowledge.drive.filters.scopes.${value}`) })),
      },
      {
        id: 'type',
        label: t('knowledge.drive.filters.type'),
        options: DRIVE_TYPES.map((value) => ({ value, label: t(`knowledge.drive.filters.types.${value}`) })),
      },
      {
        id: 'created_by',
        label: t('knowledge.drive.filters.createdBy'),
        options: (data?.createdByOptions ?? []).map((value) => ({ value, label: value })),
      },
    ],
    [data?.createdByOptions, t],
  );
  const sortDefs = useMemo(() => [{ value: DRIVE_SORT_FIELD, label: t('knowledge.drive.sort.recent') }], [t]);

  const openAllFilters = useCallback(() => {
    filtersState.openDraft();
    setIsAllFiltersOpen(true);
  }, [filtersState]);
  const closeAllFilters = useCallback(() => setIsAllFiltersOpen(false), []);
  const openNewFolder = useCallback(() => setIsNewFolderOpen(true), []);
  const closeNewFolder = useCallback(() => setIsNewFolderOpen(false), []);
  const clearEverything = useCallback(() => {
    clearAll();
    setQuickFilter('all');
  }, [clearAll, setQuickFilter]);

  const items = data?.items ?? [];
  const { rangeLabel, labels: paginationLabels } = usePaginationLabels({ meta, itemCount: items.length });

  const settled = !loading && !error && !accessDenied;
  const showPagination = settled && meta.totalItems > 0;

  const results = () => {
    if (loading) return <EntityListSkeleton perPage={pageSize} />;
    if (accessDenied) return <AccessDeniedState />;
    if (error) return <ErrorState message={error} onRetry={refetch} />;
    if (!items.length) {
      const narrowed = hasActiveQuery || quickFilter !== 'all';
      return narrowed ? (
        <EmptyState
          testId="no-results-state"
          icon={SearchX}
          title={t('knowledge.drive.noResults.title')}
          subtitle={t('knowledge.drive.noResults.subtitle')}
          action={
            <Button variant="secondary" size="sm" onClick={clearEverything}>
              {t('knowledge.drive.noResults.clear')}
            </Button>
          }
        />
      ) : (
        <EmptyState icon={FolderOpen} title={t('knowledge.drive.empty.title')} subtitle={t('knowledge.drive.empty.subtitle')} />
      );
    }
    return (
      <div className="flex flex-col gap-3">
        {items.map((item) => (
          <DriveItemCard key={item.id} item={item} onOpenFolder={openFolder} onViewClaims={viewClaims} />
        ))}
      </div>
    );
  };

  return (
    <div
      data-testid="drive-page"
      data-folder-id={folderId}
      className={['grid min-w-0 flex-1 grid-cols-1 gap-6 lg:grid-cols-[220px_minmax(0,1fr)]', showPagination ? '' : 'pb-6']
        .filter(Boolean)
        .join(' ')}
    >
      {data?.tree?.length ? <DriveTree tree={data.tree} currentId={folderId} onOpen={openFolder} /> : <div />}

      <div className="flex min-w-0 flex-col gap-4">
        {/* One card, as in the prototype: the drive, its actions and the extraction switch. */}
        {data ? (
          <div data-testid="drive-overview">
            <CartContainer size="M" variant="neutral">
              <div className="flex flex-col gap-4">
                <DriveHeader
                  driveName={data.driveName}
                  folder={data.folder}
                  breadcrumb={data.breadcrumb}
                  canManage={data.canManage}
                  onNavigate={openFolder}
                  onNewFolder={openNewFolder}
                />
                {data.extraction ? (
                  <DriveExtractionToggle
                    name={currentName}
                    extraction={data.extraction}
                    canManage={data.canManage}
                    saving={savingExtraction}
                    onChange={handleExtraction}
                  />
                ) : null}
              </div>
            </CartContainer>
          </div>
        ) : null}

        <DataListFilterBar
          filtersState={filtersState}
          filterDefs={filterDefs}
          sortDefs={sortDefs}
          searchPlaceholder={t('knowledge.drive.searchPlaceholder')}
          onAllFiltersClick={openAllFilters}
        />
        <KnowledgeQuickFilters
          ids={DRIVE_QUICK_FILTERS}
          labelPrefix="knowledge.drive.quickFilters"
          value={quickFilter}
          onChange={setQuickFilter}
        />

        <div className="flex min-w-0 flex-1 flex-col">{results()}</div>

        {showPagination ? (
          <div className="sticky bottom-0 z-10 mt-auto bg-surface-base pb-6">
            <Pagination
              rangeLabel={rangeLabel}
              totalItems={meta.totalItems}
              currentPage={meta.currentPage}
              totalPages={meta.totalPages}
              hasPrevious={meta.hasPrevious}
              hasNext={meta.hasNext}
              perPage={pageSize}
              pageSizeOptions={DRIVE_PAGE_SIZE_OPTIONS}
              onPrevPage={handlePrevPage}
              onNextPage={handleNextPage}
              onPageSizeChange={handlePageSizeChange}
              disabled={loading}
              ariaLabelPerPage={t('list.pagination.rowsPerPage')}
              labels={paginationLabels}
            />
          </div>
        ) : null}
      </div>

      <DataListFilterSlideout
        isOpen={isAllFiltersOpen}
        onClose={closeAllFilters}
        filtersState={filtersState}
        filterDefs={filterDefs}
        sortDefs={sortDefs}
      />
      {isNewFolderOpen && data ? (
        <NewFolderSlideOut
          entityType={entityType}
          entityId={entityId}
          parentId={folderId}
          parentName={currentName}
          onClose={closeNewFolder}
          onCreated={refresh}
        />
      ) : null}
    </div>
  );
}

export default DrivePage;
